import {
    ErDiagramData,
    ErEntity,
    ErRelationship,
    ErAttribute,
    ErConnection
} from '../model/ErModel';
import { DesignTestsConfig, RubricItem } from '../model/DesignTestConfig';

export interface ValidationFeedbackItem {
    type: 'success' | 'error' | 'warning';
    category: 'entity' | 'relationship' | 'attribute' | 'connection' | 'hierarchy';
    diffType?: 'missing' | 'surplus' | 'mismatch' | 'correct';
    itemLabel?: string;
    expected?: string;
    actual?: string;
    message: string;
    targetId?: string;
    pointsEarned?: number;
    maxPoints?: number;
}

export interface ValidationResult {
    score: number; // 0 a 100
    notaBase?: number; // Puntuació directa de la rúbrica (0-100)
    penalitzacioTotal?: number; // Punts restats per intents fallits
    intentsFallitsPrevis?: number;
    isCorrect: boolean;
    feedback: ValidationFeedbackItem[];
    summary: {
        entitiesExpected: number;
        entitiesMatched: number;
        relationshipsExpected: number;
        relationshipsMatched: number;
        attributesExpected: number;
        attributesMatched: number;
        connectionsExpected: number;
        connectionsMatched: number;
    };
}

// Normalització tolerant de noms: minúscules, sense accents, sense espais/guions,
// i amb despluralització simple (treu 's'/'es'/'os' finals) perquè "Mascotes" ≈ "MASCOTA".
const normalize = (str?: string): string => {
    let s = (str || '')
        .trim()
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // treure accents
        .replace(/[\s_\-]+/g, ''); // treure espais, guions baixos i guions
    // Desplural simple (català/castellà): "mascotes"->"mascot", "coches"->"coch", "gats"->"gat"
    if (s.length > 3) {
        if (s.endsWith('es')) s = s.slice(0, -2);
        else if (s.endsWith('s')) s = s.slice(0, -1);
    }
    return s;
};

/**
 * Valida un diagrama ER de l'estudiant respecte la solució oficial del professor
 * Suporta rúbrica configurable i penalització per intents fallits previs.
 */
export const validateErDiagram = (
    studentData: ErDiagramData,
    solutionData: ErDiagramData,
    customConfig?: DesignTestsConfig | null,
    intentsFallitsPrevis: number = 0
): ValidationResult => {
    const feedback: ValidationFeedbackItem[] = [];

    const solEntities: ErEntity[] = solutionData.entities || [];
    const stuEntities: ErEntity[] = studentData.entities || [];
    const solRels: ErRelationship[] = solutionData.relationships || [];
    const stuRels: ErRelationship[] = studentData.relationships || [];
    const solAttrs: ErAttribute[] = solutionData.attributes || [];
    const stuAttrs: ErAttribute[] = studentData.attributes || [];
    const solConns: ErConnection[] = solutionData.connections || [];
    const stuConns: ErConnection[] = studentData.connections || [];

    // Carregar rúbrica si n'hi ha
    const rubricMap = new Map<string, RubricItem>();
    // Índex normalitzat: permet casar ítems de rúbrica desats amb formats d'ID antics
    // (p. ex. "e_PROPIETARI") amb els IDs que genera el validador actual ("entity_e_propietari").
    // Normalitzem traient prefixos coneguts i passant a minúscules.
    const rubricNormMap = new Map<string, RubricItem>();
    const normalizeRubricKey = (raw: string): string => {
        let k = (raw || '').toLowerCase();
        k = k.replace(/^(entity_|rel_|attr_|conn_|hier_|table_|column_|pk_|fk_|rel_table_|rel_col_|rel_fk_|e_|r_|a_|c_)/, '');
        return k.trim();
    };
    const hasCustomRubric = Boolean(customConfig && Array.isArray(customConfig.items) && customConfig.items.length > 0);
    if (hasCustomRubric && customConfig?.items) {
        customConfig.items.forEach(it => {
            rubricMap.set(it.id, it);
            rubricNormMap.set(normalizeRubricKey(it.id), it);
            // També indexem per targetKey i label normalitzats si existeixen
            if ((it as any).targetKey) rubricNormMap.set(normalizeRubricKey(String((it as any).targetKey)), it);
        });
    }

    let earnedScore = 0;
    let totalMaxConfigured = 0;

    // Helper per comprovar si un element s'ha d'avaluar. Accepta un o diversos IDs/claus
    // candidats (ex: l'ID canònic i el nom de l'element) i fa matching tolerant.
    const checkRubric = (candidates: string | string[], defaultMax = 0): { enabled: boolean; points: number } => {
        if (!hasCustomRubric) {
            return { enabled: true, points: defaultMax };
        }
        const list = Array.isArray(candidates) ? candidates : [candidates];
        let item: RubricItem | undefined;
        for (const c of list) {
            item = rubricMap.get(c) || rubricNormMap.get(normalizeRubricKey(c));
            if (item) break;
        }
        if (!item || !item.enabled) {
            return { enabled: false, points: 0 };
        }
        return { enabled: true, points: Number(item.points) || 0 };
    };

    // 1. Validar Entitats
    let matchedEntities = 0;
    const entityIdMap = new Map<string, string>(); // solId -> stuId

    solEntities.forEach(solE => {
        const rubricId = `entity_${solE.id || solE.name}`;
        const rubric = checkRubric([rubricId, `entity_${solE.name}`, solE.id, solE.name], 30);
        if (rubric.enabled) totalMaxConfigured += rubric.points;

        const found = stuEntities.find(stuE => normalize(stuE.name) === normalize(solE.name));
        if (!found) {
            if (rubric.enabled) {
                feedback.push({
                    type: 'error',
                    category: 'entity',
                    diffType: 'missing',
                    itemLabel: solE.name,
                    expected: `Entitat "${solE.name}" (${solE.isWeak ? 'dèbil' : 'regular'})`,
                    actual: '(No entregada)',
                    message: `Falta l'entitat "${solE.name}".`,
                    maxPoints: rubric.points,
                    pointsEarned: 0
                });
            }
        } else {
            entityIdMap.set(solE.id, found.id);
            if (Boolean(solE.isWeak) !== Boolean(found.isWeak)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        category: 'entity',
                        diffType: 'mismatch',
                        itemLabel: solE.name,
                        expected: solE.isWeak ? 'Entitat dèbil' : 'Entitat regular',
                        actual: found.isWeak ? 'Entitat dèbil' : 'Entitat regular',
                        message: `L'entitat "${solE.name}" hauria de ser ${solE.isWeak ? 'dèbil' : 'regular'}.`,
                        maxPoints: rubric.points,
                        pointsEarned: 0
                    });
                }
            } else {
                matchedEntities++;
                if (rubric.enabled) {
                    earnedScore += rubric.points;
                    feedback.push({
                        type: 'success',
                        category: 'entity',
                        diffType: 'correct',
                        itemLabel: solE.name,
                        expected: `Entitat "${solE.name}"`,
                        actual: `Entitat "${found.name}"`,
                        message: `Entitat "${solE.name}" correcta (+${rubric.points} pts).`,
                        maxPoints: rubric.points,
                        pointsEarned: rubric.points
                    });
                }
            }
        }
    });

    // Detectar entitats sobrants de l'estudiant
    stuEntities.forEach(stuE => {
        if (!solEntities.some(solE => normalize(solE.name) === normalize(stuE.name))) {
            feedback.push({
                type: 'warning',
                category: 'entity',
                diffType: 'surplus',
                itemLabel: stuE.name,
                expected: '(No requerida a la solució)',
                actual: `Entitat "${stuE.name}"`,
                message: `L'entitat "${stuE.name}" no és requerida a la solució.`
            });
        }
    });

    // 2. Validar Relacions
    // IMPORTANT: el NOM de la relació NO importa. Casem cada relació per la seva ESTRUCTURA:
    // el conjunt d'entitats que connecta (via connections). El que s'avalua és la cardinalitat
    // i la participació dels enllaços, no com es digui la relació.
    let matchedRels = 0;
    const relIdMap = new Map<string, string>(); // solId -> stuId

    // Retorna el conjunt de noms (normalitzats) d'entitats veïnes d'una relació,
    // seguint les connexions d'aquell diagrama.
    const entityNamesOfRel = (
        relId: string,
        conns: ErConnection[],
        entities: ErEntity[],
        rels: ErRelationship[]
    ): string[] => {
        const names: string[] = [];
        conns.forEach(c => {
            let otherId: string | null = null;
            if (c.from === relId) otherId = c.to;
            else if (c.to === relId) otherId = c.from;
            if (otherId) {
                const ent = entities.find(e => e.id === otherId);
                if (ent) names.push(normalize(ent.name));
            }
        });
        return names.sort();
    };

    const relNeighborsKey = (
        relId: string,
        conns: ErConnection[],
        entities: ErEntity[],
        rels: ErRelationship[]
    ): string => entityNamesOfRel(relId, conns, entities, rels).join('|');

    const usedStuRelIds = new Set<string>();

    solRels.forEach(solR => {
        const rubricId = `rel_${solR.id || solR.name}`;
        const rubric = checkRubric([rubricId, `rel_${solR.name}`, solR.id, solR.name], 30);
        if (rubric.enabled) totalMaxConfigured += rubric.points;

        const solKey = relNeighborsKey(solR.id, solConns, solEntities, solRels);

        // Busquem una relació de l'alumne encara no usada que connecti EL MATEIX conjunt
        // d'entitats (per nom). El nom de la relació és irrellevant.
        let found = stuRels.find(stuR =>
            !usedStuRelIds.has(stuR.id) &&
            relNeighborsKey(stuR.id, stuConns, stuEntities, stuRels) === solKey &&
            solKey !== ''
        );
        // Fallback: si l'estructura de connexions no permet casar (p. ex. manquen connexions),
        // intentem per nom com abans per no perdre coincidències evidents.
        if (!found) {
            found = stuRels.find(stuR => !usedStuRelIds.has(stuR.id) && normalize(stuR.name) === normalize(solR.name));
        }

        const entitatsText = entityNamesOfRel(solR.id, solConns, solEntities, solRels).join(' ⟷ ') || solR.name;

        if (!found) {
            if (rubric.enabled) {
                feedback.push({
                    type: 'error',
                    category: 'relationship',
                    diffType: 'missing',
                    itemLabel: entitatsText,
                    expected: `Relació entre [${entitatsText}] (${solR.cardinality || 'cardinalitat no fixada'})`,
                    actual: '(No trobada cap relació que connecti aquestes entitats)',
                    message: `Falta la relació entre [${entitatsText}].`,
                    maxPoints: rubric.points,
                    pointsEarned: 0
                });
            }
        } else {
            usedStuRelIds.add(found.id);
            relIdMap.set(solR.id, found.id);
            let relErrors = false;
            if (Boolean(solR.isWeak) !== Boolean(found.isWeak)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        category: 'relationship',
                        diffType: 'mismatch',
                        itemLabel: entitatsText,
                        expected: solR.isWeak ? 'Relació identificadora / dèbil' : 'Relació regular',
                        actual: found.isWeak ? 'Relació dèbil' : 'Relació regular',
                        message: `La relació entre [${entitatsText}] hauria de ser ${solR.isWeak ? 'dèbil' : 'regular'}.`
                    });
                }
                relErrors = true;
            }
            if (solR.cardinality && found.cardinality !== solR.cardinality) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'error',
                        category: 'relationship',
                        diffType: 'mismatch',
                        itemLabel: `${entitatsText} (cardinalitat)`,
                        expected: solR.cardinality,
                        actual: found.cardinality || 'indefinida',
                        message: `La cardinalitat de la relació [${entitatsText}] és ${found.cardinality || 'indefinida'}, s'esperava ${solR.cardinality}.`,
                        maxPoints: rubric.points,
                        pointsEarned: 0
                    });
                }
                relErrors = true;
            }

            if (!relErrors) {
                matchedRels++;
                if (rubric.enabled) {
                    earnedScore += rubric.points;
                    feedback.push({
                        type: 'success',
                        category: 'relationship',
                        diffType: 'correct',
                        itemLabel: entitatsText,
                        expected: `Relació entre [${entitatsText}] (${solR.cardinality || ''})`,
                        actual: `Relació "${found.name}" (${found.cardinality || ''})`,
                        message: `Relació entre [${entitatsText}] correcta amb cardinalitat ${solR.cardinality} (+${rubric.points} pts).`,
                        maxPoints: rubric.points,
                        pointsEarned: rubric.points
                    });
                }
            }
        }
    });

    // Detectar relacions sobrants (les de l'alumne que no s'han pogut casar per estructura)
    stuRels.forEach(stuR => {
        if (!usedStuRelIds.has(stuR.id)) {
            feedback.push({
                type: 'warning',
                category: 'relationship',
                diffType: 'surplus',
                itemLabel: stuR.name,
                expected: '(No requerida a la solució)',
                actual: `Relació "${stuR.name}"`,
                message: `La relació "${stuR.name}" no es correspon amb cap relació de la solució.`
            });
        }
    });

    // 3. Validar Atributs i claus primàries (PK)
    let matchedAttrs = 0;
    solAttrs.forEach(solA => {
        const parentSolEntity = solEntities.find(e => e.id === solA.parentId);
        const parentSolRel = solRels.find(r => r.id === solA.parentId);
        const expectedParentName = parentSolEntity ? parentSolEntity.name : (parentSolRel ? parentSolRel.name : '');

        const rubricId = `attr_${solA.id || (expectedParentName + '_' + solA.name)}`;
        const rubric = checkRubric([rubricId, `attr_${expectedParentName}_${solA.name}`, `attr_${solA.name}`, solA.id, solA.name], 20);
        if (rubric.enabled) totalMaxConfigured += rubric.points;

        const stuParentId = parentSolEntity ? entityIdMap.get(parentSolEntity.id) : (parentSolRel ? relIdMap.get(parentSolRel.id) : undefined);
        const found = stuAttrs.find(stuA => normalize(stuA.name) === normalize(solA.name) && (!stuParentId || stuA.parentId === stuParentId));

        if (!found) {
            if (rubric.enabled) {
                feedback.push({
                    type: 'error',
                    category: 'attribute',
                    diffType: 'missing',
                    itemLabel: `${expectedParentName}.${solA.name}`,
                    expected: `Atribut "${solA.name}" ${solA.isPK ? '[PK]' : ''} a "${expectedParentName}"`,
                    actual: '(No entregat)',
                    message: `Falta l'atribut "${solA.name}" a "${expectedParentName}".`,
                    maxPoints: rubric.points,
                    pointsEarned: 0
                });
            }
        } else {
            let attrOk = true;
            if (Boolean(solA.isPK) !== Boolean(found.isPK)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'error',
                        category: 'attribute',
                        diffType: 'mismatch',
                        itemLabel: `${expectedParentName}.${solA.name} (PK)`,
                        expected: solA.isPK ? 'Clau Primària (PK)' : 'No PK',
                        actual: found.isPK ? 'Clau Primària (PK)' : 'No PK',
                        message: `L'atribut "${solA.name}" ${solA.isPK ? 'ha de ser' : 'no ha de ser'} Clau Primària (PK).`
                    });
                }
                attrOk = false;
            }
            if (Boolean(solA.isWeakPK) !== Boolean(found.isWeakPK)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        category: 'attribute',
                        diffType: 'mismatch',
                        itemLabel: `${expectedParentName}.${solA.name} (Clau Parcial)`,
                        expected: solA.isWeakPK ? 'Clau parcial/dèbil' : 'Atribut regular',
                        actual: found.isWeakPK ? 'Clau parcial/dèbil' : 'Atribut regular',
                        message: `L'atribut "${solA.name}" ${solA.isWeakPK ? 'ha de ser' : 'no ha de ser'} clau parcial/dèbil.`
                    });
                }
                attrOk = false;
            }
            if (Boolean(solA.isMultivalued) !== Boolean(found.isMultivalued)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        category: 'attribute',
                        diffType: 'mismatch',
                        itemLabel: `${expectedParentName}.${solA.name} (Multivaluat)`,
                        expected: solA.isMultivalued ? 'Multivaluat' : 'Simple',
                        actual: found.isMultivalued ? 'Multivaluat' : 'Simple',
                        message: `L'atribut "${solA.name}" ${solA.isMultivalued ? 'ha de ser' : 'no ha de ser'} multivaluat.`
                    });
                }
                attrOk = false;
            }
            if (Boolean(solA.isDerived) !== Boolean(found.isDerived)) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        category: 'attribute',
                        diffType: 'mismatch',
                        itemLabel: `${expectedParentName}.${solA.name} (Derivat)`,
                        expected: solA.isDerived ? 'Derivat' : 'Emmagatzemat',
                        actual: found.isDerived ? 'Derivat' : 'Emmagatzemat',
                        message: `L'atribut "${solA.name}" ${solA.isDerived ? 'ha de ser' : 'no ha de ser'} derivat.`
                    });
                }
                attrOk = false;
            }

            if (attrOk) {
                matchedAttrs++;
                if (rubric.enabled) {
                    earnedScore += rubric.points;
                    feedback.push({
                        type: 'success',
                        category: 'attribute',
                        diffType: 'correct',
                        itemLabel: `${expectedParentName}.${solA.name}`,
                        expected: `Atribut "${solA.name}"`,
                        actual: `Atribut "${found.name}"`,
                        message: `Atribut "${solA.name}" a "${expectedParentName}" correcte (+${rubric.points} pts).`,
                        maxPoints: rubric.points,
                        pointsEarned: rubric.points
                    });
                }
            } else if (rubric.enabled) {
                feedback.push({
                    type: 'error',
                    category: 'attribute',
                    diffType: 'mismatch',
                    itemLabel: `${expectedParentName}.${solA.name}`,
                    expected: 'Totes les propietats correctes',
                    actual: 'Propietats diferents a la solució',
                    message: `Propietats incorrectes a l'atribut "${solA.name}".`,
                    maxPoints: rubric.points,
                    pointsEarned: 0
                });
            }
        }
    });

    // Detectar atributs sobrants de l'estudiant
    stuAttrs.forEach(stuA => {
        const parentStuEntity = stuEntities.find(e => e.id === stuA.parentId);
        const parentStuRel = stuRels.find(r => r.id === stuA.parentId);
        const parentName = parentStuEntity ? parentStuEntity.name : (parentStuRel ? parentStuRel.name : 'Element desconegut');

        const solParent = solEntities.find(e => normalize(e.name) === normalize(parentName)) ||
                          solRels.find(r => normalize(r.name) === normalize(parentName));
        const existsInSol = solParent && solAttrs.some(sa => sa.parentId === solParent.id && normalize(sa.name) === normalize(stuA.name));

        if (!existsInSol) {
            feedback.push({
                type: 'warning',
                category: 'attribute',
                diffType: 'surplus',
                itemLabel: `${parentName}.${stuA.name}`,
                expected: '(No requerit a la solució)',
                actual: `Atribut "${stuA.name}"`,
                message: `A "${parentName}", l'atribut "${stuA.name}" no és requerit a la solució.`
            });
        }
    });

    // 4. Validar Participacions (sobre cada RELACIÓ, no sobre connexions soltes)
    // La unitat avaluable és la relació: ha d'unir les mateixes entitats (el nom de la relació
    // és irrellevant) i les participacions cap a cada entitat han de coincidir. Als missatges
    // fem servir el nom de la relació que ha posat L'ALUMNE (si en té) i, si no existeix cap
    // relació equivalent, el nom de la relació de la solució.
    let matchedConns = 0;

    // Nom llegible d'una relació: prioritza el nom real; si no, la descriu per les entitats.
    const relDisplayName = (
        rel: ErRelationship | undefined,
        entities: ErEntity[],
        rels: ErRelationship[],
        conns: ErConnection[]
    ): string => {
        if (!rel) return 'relació';
        if (rel.name && rel.name.trim()) return rel.name.trim();
        const veins = entityNamesVisible(rel.id, conns, entities);
        return veins.length > 0 ? `relació (${veins.join('–')})` : 'relació';
    };

    // Noms (sense normalitzar, per mostrar) de les entitats veïnes d'una relació.
    function entityNamesVisible(relId: string, conns: ErConnection[], entities: ErEntity[]): string[] {
        const out: string[] = [];
        conns.forEach(c => {
            const other = c.from === relId ? c.to : (c.to === relId ? c.from : null);
            if (other) {
                const ent = entities.find(e => e.id === other);
                if (ent) out.push(ent.name);
            }
        });
        return out;
    }

    // Mapa entitat(normalitzada) -> participació, per a una relació d'un diagrama.
    const participacionsDeRel = (
        relId: string,
        conns: ErConnection[],
        entities: ErEntity[]
    ): Map<string, string | undefined> => {
        const m = new Map<string, string | undefined>();
        conns.forEach(c => {
            const other = c.from === relId ? c.to : (c.to === relId ? c.from : null);
            if (other) {
                const ent = entities.find(e => e.id === other);
                if (ent) m.set(normalize(ent.name), c.participation);
            }
        });
        return m;
    };

    // Comptem quantes participacions "unitat" hi ha a la solució (per repartir pes estructural).
    let totalParticipacions = 0;

    solRels.forEach((solR, idx) => {
        const relNameSol = relDisplayName(solR, solEntities, solRels, solConns);
        const stuRelId = relIdMap.get(solR.id); // relació de l'alumne casada per estructura
        const stuRel = stuRels.find(r => r.id === stuRelId);
        // Nom a mostrar: el que ha posat l'alumne si existeix; si no, el de la solució.
        const relNameMostrat = stuRel ? relDisplayName(stuRel, stuEntities, stuRels, stuConns) : relNameSol;

        const solParts = participacionsDeRel(solR.id, solConns, solEntities);
        const stuParts = stuRelId ? participacionsDeRel(stuRelId, stuConns, stuEntities) : new Map<string, string | undefined>();

        solParts.forEach((solPart, entNormKey) => {
            totalParticipacions++;
            // Nom visible de l'entitat (desnormalitzat): el busquem a la solució.
            const entName = solEntities.find(e => normalize(e.name) === entNormKey)?.name || entNormKey;

            const rubricId = `conn_${relNameSol}_${entName}_${idx}`;
            const rubric = checkRubric([rubricId, `conn_${entName}_${relNameSol}`, `conn_${relNameSol}`], 20);
            if (rubric.enabled) totalMaxConfigured += rubric.points;

            if (!stuRelId) {
                // L'alumne no té cap relació equivalent que uneixi aquestes entitats.
                if (rubric.enabled) {
                    feedback.push({
                        type: 'error',
                        category: 'connection',
                        diffType: 'missing',
                        itemLabel: `${entName} ⟷ ${relNameSol}`,
                        expected: `Participació ${solPart || '(definida)'} entre "${entName}" i la relació "${relNameSol}"`,
                        actual: '(No existeix la relació a l\'entrega)',
                        message: `Falta la relació "${relNameSol}" que uneix "${entName}" (amb participació ${solPart || 'definida'}).`,
                        maxPoints: rubric.points,
                        pointsEarned: 0
                    });
                }
                return;
            }

            const teEntitat = stuParts.has(entNormKey);
            const stuPart = stuParts.get(entNormKey);

            if (!teEntitat) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'error',
                        category: 'connection',
                        diffType: 'missing',
                        itemLabel: `${entName} ⟷ ${relNameMostrat}`,
                        expected: `Enllaç "${entName}" ⟷ "${relNameMostrat}"${solPart ? ` amb participació ${solPart}` : ''}`,
                        actual: '(L\'entitat no està enllaçada amb aquesta relació)',
                        message: `Falta enllaçar "${entName}" amb la relació "${relNameMostrat}".`,
                        maxPoints: rubric.points,
                        pointsEarned: 0
                    });
                }
                return;
            }

            if (solPart && stuPart !== solPart) {
                if (rubric.enabled) {
                    feedback.push({
                        type: 'error',
                        category: 'connection',
                        diffType: 'mismatch',
                        itemLabel: `${entName} ⟷ ${relNameMostrat}`,
                        expected: solPart,
                        actual: stuPart || '(cap)',
                        message: stuPart
                            ? `Participació incorrecta entre "${entName}" i la relació "${relNameMostrat}": és ${stuPart}, s'esperava ${solPart}.`
                            : `Falta la participació entre "${entName}" i la relació "${relNameMostrat}" (s'esperava ${solPart}).`,
                        maxPoints: rubric.points,
                        pointsEarned: 0
                    });
                }
                return;
            }

            // Correcte
            matchedConns++;
            if (rubric.enabled) {
                earnedScore += rubric.points;
                feedback.push({
                    type: 'success',
                    category: 'connection',
                    diffType: 'correct',
                    itemLabel: `${entName} ⟷ ${relNameMostrat}`,
                    expected: `Participació ${solPart || '(no exigida)'}`,
                    actual: `Participació ${stuPart || '(cap)'}`,
                    message: `Participació correcta entre "${entName}" i la relació "${relNameMostrat}"${solPart ? ` (${solPart})` : ''} (+${rubric.points} pts).`,
                    maxPoints: rubric.points,
                    pointsEarned: rubric.points
                });
            }
        });
    });

    // Detectar si la solució del professor està buida (no hi ha res amb què comparar).
    // En aquest cas NO podem donar 100: significa que l'exercici no té solució configurada.
    const solucioBuida = solEntities.length === 0 && solRels.length === 0
        && solAttrs.length === 0 && solConns.length === 0;
    if (solucioBuida) {
        feedback.push({
            type: 'error',
            category: 'entity',
            message: 'No hi ha solució de referència configurada per a aquest exercici. No es pot avaluar automàticament (0/100).'
        });
    }

    // Nota estructural (comparació per pesos per defecte), independent de la rúbrica.
    // Les participacions es compten com a unitats (totalParticipacions) en lloc de connexions soltes.
    const totalWeight = (solEntities.length * 30) + (solRels.length * 30) + (solAttrs.length * 20) + (totalParticipacions * 20);
    const actualScore = (matchedEntities * 30) + (matchedRels * 30) + (matchedAttrs * 20) + (matchedConns * 20);
    const notaEstructural = totalWeight > 0 ? Math.round((actualScore / totalWeight) * 100) : 0;

    // Càlcul de la nota base (0 a 100).
    let notaBase = 0;
    if (solucioBuida) {
        notaBase = 0;
    } else if (hasCustomRubric && totalMaxConfigured > 0) {
        // Només fem servir la rúbrica si realment aporta punts avaluables. Si la rúbrica
        // existeix però cap ítem casa amb la solució (IDs desfasats) o tots valen 0 punts,
        // caiem a la nota estructural per no donar un 0 injustificat.
        notaBase = Math.round((earnedScore / totalMaxConfigured) * 100);
    } else {
        notaBase = notaEstructural;
    }


    // Aplicar penalització per intents fallits previs
    const penalitzacioPerIntent = Number(customConfig?.penalitzacioPerIntent) || 0;
    const penalitzacioTotal = penalitzacioPerIntent * Math.max(0, intentsFallitsPrevis);
    const scoreFinal = Math.max(0, Math.min(100, Math.round(notaBase - penalitzacioTotal)));

    if (penalitzacioTotal > 0) {
        feedback.push({
            type: 'warning',
            category: 'connection',
            message: `Penalització aplicada: -${penalitzacioTotal} punts (-${penalitzacioPerIntent} pts per ${intentsFallitsPrevis} intents fallits previs).`
        });
    }

    if (feedback.length === 0) {
        feedback.push({
            type: 'success',
            category: 'entity',
            message: 'Model Entitat-Relació completament correcte!'
        });
    }

    return {
        score: scoreFinal,
        notaBase,
        penalitzacioTotal,
        intentsFallitsPrevis,
        isCorrect: scoreFinal === 100 && feedback.every(f => f.type !== 'error'),
        feedback,
        summary: {
            entitiesExpected: solEntities.length,
            entitiesMatched: matchedEntities,
            relationshipsExpected: solRels.length,
            relationshipsMatched: matchedRels,
            attributesExpected: solAttrs.length,
            attributesMatched: matchedAttrs,
            connectionsExpected: totalParticipacions,
            connectionsMatched: matchedConns
        }
    };
};
